/**
 * CARTIEAI · Engineered Stack — Interactive
 * ==========================================
 * Each engine card opens a Dialog with a concrete proof artifact (code
 * snippet, JSON I/O, decision table, sequence chain). "Show, don't tell"
 * for HN visitors — no navigation required to see what each engine does.
 *
 * Feb 25 2026 — Interactive upgrade. Previously a static link grid.
 */
import React, { useState } from 'react';
import SEOHead from '../components/SEOHead';
import { Link } from 'react-router-dom';
import {
  SearchCode, GitBranch, ArrowRight, Box, Handshake, Tags,
  ArrowRightLeft, Shield, MessageSquare, Bot, Calculator, TrendingUp,
  Activity, Landmark, Leaf, Cpu, Sparkles, X, ExternalLink, Code2,
  Network, Users, FileSearch, Lock, CheckCircle2,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ENGINE_COUNT } from '../constants/product';

// =============================================================================
// Proof artifacts — each engine ships with ONE concrete demo payload.
// =============================================================================
const PROOFS = {
  '/cartie-lens': {
    label: 'Sample lint output',
    type: 'code',
    language: 'js',
    body: `// Detected by CARTIE Lens on PR #4421
// File: api/orders.controller.ts:42

for (const order of orders) {
  // ⚠️ N+1 query — 1,200 orders → 1,200 separate DB roundtrips
  const customer = await db.customers.findById(order.customer_id);
  order.customer = customer;
}

// ╭─ Cost impact ────────────────────────╮
// │ RDS read IOPS:    +12,400/day        │
// │ Estimated cost:   +$315/month        │
// │ Fix:              JOIN or .find({_id: {$in: ids}})  │
// │ Confidence:       0.97                │
// ╰──────────────────────────────────────╯`,
  },
  '/cost-bisect': {
    label: 'Bisect output',
    type: 'code',
    language: 'bash',
    body: `$ cartie bisect cost --metric=daily_cost --baseline=2026-02-01 --head=HEAD

Searching 47 commits...
[1/47] checkout 8f2c1e0  daily_cost=$1,840  (baseline)
[24/47] checkout a912de4  daily_cost=$1,855  ✓ ok
[36/47] checkout c4f1ba8  daily_cost=$2,490  ✗ regression
[35/47] checkout 7b290c2  daily_cost=$2,485  ✗ regression
[34/47] checkout 1d8af31  daily_cost=$1,860  ✓ ok

──────────────────────────────────────────────
First bad commit: 7b290c2
Author:           sarah@example.com
Title:            "Add background reindex job"
Cost delta:       +$625/day  (+34%)
Root cause:       reindex runs on m5.4xlarge every 15min instead of nightly
Suggested fix:    Reduce cadence to cron 0 3 * * * — saves $580/day
──────────────────────────────────────────────`,
  },
  '/cost-causality': {
    label: 'Causality decomposition',
    type: 'json',
    body: {
      period: '2026-02-01 to 2026-02-24',
      total_cost_delta_usd: 12480,
      decomposition: {
        organic_growth: { usd: 6240, pct: 50, confidence_ci: '±8%' },
        new_features: { usd: 2820, pct: 23, signal: 'feature_flag=ai_summary launched 2026-02-08' },
        inefficiency: { usd: 3120, pct: 25, breakdown: {
          unattached_ebs: 412,
          oversized_rds: 1880,
          orphan_lambda_logs: 828,
        }},
        seasonality: { usd: 300, pct: 2 },
      },
      sla: 'Decomposition completed in 1.8s',
    },
  },
  '/causal-bot': {
    label: 'Natural-language chain',
    type: 'chain',
    body: [
      { actor: 'User', text: 'Why did our AWS bill jump 14% this week?' },
      { actor: 'Bot', text: '→ Calling Cost-Bisect to isolate the regression commit…' },
      { actor: 'Bot', text: '→ Found: PR #4321 added a Snowflake export running hourly' },
      { actor: 'Bot', text: '→ Calling Causality to confirm: 92% of delta = this PR' },
      { actor: 'Bot', text: '→ Calling P&L to attribute: feature "weekly_digest", margin -120%' },
      { actor: 'Bot', text: '→ Recommendation: reduce cadence to daily — saves $1,180/mo (PR ready)' },
    ],
  },
  '/decision-simulator': {
    label: 'Scenario simulation',
    type: 'table',
    headers: ['Scenario', 'Δ $/mo', 'Risk band', 'SLI impact', 'Verdict'],
    rows: [
      ['Migrate prod RDS m5.xlarge → m5.large', '−$280', 'Low', '+4ms p99', '✓ Recommended'],
      ['Switch to Reserved Instances (3yr)', '−$1,840', 'High', 'none', '⚠ Locks in 36mo'],
      ['Move S3 → Glacier IR for cold tier', '−$640', 'Low', '+12h restore', '✓ Recommended'],
      ['Spot instances for batch ETL', '−$420', 'Med', '8% interrupt rate', '✓ Conditional'],
    ],
  },
  '/budget-negotiator': {
    label: 'Sample contract.json',
    type: 'code',
    language: 'json',
    body: `{
  "envelope_id": "env_payments_q1_2026",
  "cost_center": "payments-api",
  "owner": "cfo@example.com",
  "quarter": "2026-Q1",
  "monthly_envelope_usd": 18000,
  "thresholds": {
    "green_auto_approve_pct": 75,
    "yellow_review_pct": 90,
    "red_renegotiate_pct": 100
  },
  "signed_by": {
    "finance": "cfo@example.com",
    "engineering": "vp-eng@example.com",
    "signed_at": "2026-01-15T14:30:00Z",
    "signature_sha": "0x4a8c..."
  },
  "auto_actions": {
    "on_green": "merge_pr",
    "on_yellow": "request_review_from_team_lead",
    "on_red": "open_renegotiation_flow"
  }
}`,
  },
  '/k8s-cost': {
    label: 'Shapley allocation',
    type: 'table',
    headers: ['Pod', 'Marginal CPU', 'Shapley share $', 'Naive prop. $', 'Δ vs. naive'],
    rows: [
      ['payments-api',   '420 mCPU',  '$840',  '$612',  '+37% (paid for shared infra)'],
      ['analytics-job',  '1.2 CPU',   '$2,310', '$2,840', '−19% (used at quiet hours)'],
      ['ml-inference',   '2.4 CPU',   '$4,180', '$4,920', '−15% (parallelisable)'],
      ['cron-cleanup',   '40 mCPU',   '$28',   '$60',    '−53% (off-peak)'],
    ],
  },
  '/reverse-tag': {
    label: 'AI-inferred cost-center',
    type: 'json',
    body: {
      input_resource: {
        arn: 'arn:aws:ec2:us-east-1:i-0a8b2c4d1f',
        name_tag: 'webapp-staging-eu-3',
        created_by: 'sarah@example.com',
        in_vpc_with: ['app-server-eu-1', 'cache-eu-2'],
      },
      inference: {
        suggested_cost_center: 'Engineering / Web Platform',
        confidence: 'high',
        signals: [
          'creator.email domain matches team: web-platform',
          'name token "webapp" matches 12 other resources tagged team=web',
          'VPC neighbours all tagged team=web-platform',
        ],
        cost_avoided_in_unallocated_bucket_usd: 312,
      },
    },
  },
  '/tag-canon': {
    label: 'Tag canonicalisation map',
    type: 'table',
    headers: ['Cloud', 'Raw tag key', 'Canonical key', 'Confidence'],
    rows: [
      ['AWS',   'cost-center',     'cost_center',  '1.00 (exact)'],
      ['AWS',   'CostCenter',      'cost_center',  '0.98 (case-fold)'],
      ['GCP',   'cc',              'cost_center',  '0.94 (token expansion)'],
      ['Azure', 'BusinessUnit',    'cost_center',  '0.91 (semantic alias)'],
      ['AWS',   'cc-id',           'cost_center',  '0.89 (suffix strip)'],
    ],
  },
  '/batch-router': {
    label: 'Sync→batch routing decision',
    type: 'json',
    body: {
      request_type: 'embedding generation',
      latency_budget: '24h',
      decision: 'route_to_batch_api',
      provider: 'openai/batch',
      cost_savings_vs_sync: '52%',
      example: {
        sync_price_per_1M: '$0.13',
        batch_price_per_1M: '$0.065',
        monthly_volume_tokens: 480_000_000,
        monthly_savings_usd: 312.0,
      },
    },
  },
  '/qa-failover': {
    label: 'Quality-adaptive failover',
    type: 'table',
    headers: ['Provider', 'Latency p95', 'BLEU score', '$/1K tokens', 'Health'],
    rows: [
      ['openai/gpt-5.2',    '1.2s', '0.87', '$0.024',   '✓ primary'],
      ['anthropic/sonnet',  '1.8s', '0.86', '$0.018',   '✓ standby'],
      ['gemini/3-flash',    '0.9s', '0.78', '$0.0008',  '⚠ quality drift'],
      ['fireworks/llama',   '2.4s', '0.74', '$0.0006',  '✗ below threshold'],
    ],
  },
  '/forward-predict': {
    label: '30-day forecast with CI',
    type: 'json',
    body: {
      horizon: '2026-02-25 → 2026-03-27',
      predicted_total_usd: 48720,
      confidence_interval_95: { low: 44_180, high: 53_260 },
      signals_used: [
        'IaC drift (Terraform plan: +2 RDS replicas)',
        'OpenTelemetry: traffic +18% wow',
        'Feature flag: ai_summary 100% rollout on day 12',
        'Historical adoption curve: SaaS onboarding pattern',
      ],
      drivers: { compute_pct: 42, database_pct: 28, llm_pct: 21, network_pct: 9 },
    },
  },
  '/unit-econ': {
    label: 'Per-request unit cost',
    type: 'json',
    body: {
      trace_id: 'trace_8f2c1e0a',
      endpoint: 'POST /api/summary/generate',
      total_cost_usd: 0.0341,
      breakdown: {
        llm_tokens: { provider: 'openai', model: 'gpt-5.2', tokens: 4_120, usd: 0.029 },
        rds_read:   { rows: 38, usd: 0.0009 },
        s3_get:     { ops: 4, usd: 0.0001 },
        embedding:  { tokens: 1_200, usd: 0.0041 },
      },
      revenue_per_request_usd: 0.058,
      margin_pct: 41.2,
    },
  },
  '/multi-currency': {
    label: 'FX-isolated P&L',
    type: 'table',
    headers: ['Region', 'Revenue (local)', '$ rev @ rate', 'Op cost USD', 'Margin'],
    rows: [
      ['EU (€)',  '€48,200',     '$52,580 @ 1.091', '$31,200', '40.6%'],
      ['UK (£)',  '£28,900',     '$36,540 @ 1.264', '$22,100', '39.5%'],
      ['JP (¥)',  '¥4,820,000', '$32,180 @ 0.0066', '$19,800', '38.5%'],
      ['IN (₹)',  '₹2,840,000', '$34,210 @ 0.0120', '$24,400', '28.7%'],
    ],
  },
  '/carbon-cost': {
    label: 'Pareto frontier',
    type: 'table',
    headers: ['Region', '$/hr', 'gCO₂/kWh', 'Pareto-optimal'],
    rows: [
      ['us-east-1',    '$0.096', '380 g', '✗ dominated'],
      ['us-west-2',    '$0.100', '200 g', '✓ frontier (low CO₂)'],
      ['eu-north-1',   '$0.105', '30 g',  '✓ frontier (lowest CO₂)'],
      ['ap-south-1',   '$0.092', '720 g', '✓ frontier (lowest $)'],
    ],
  },
  '/scope3': {
    label: 'Scope-3 per-workload',
    type: 'json',
    body: {
      workload: 'ml-training-batch-1',
      scope_2_kg_co2: 14.2,
      scope_3_breakdown_kg: {
        hardware_embodied: 8.4,
        vendor_emissions: { openai: 2.1, snowflake: 1.6, github: 0.4 },
        cooling_overhead: 3.7,
      },
      total_kg_co2: 30.4,
      vs_industry_avg: '−18% (region-arbitraged + batch-optimized)',
    },
  },
  '/gpu-spot': {
    label: 'Bid curve optimization',
    type: 'table',
    headers: ['Bid %', '$/hr', 'Interrupt prob/hr', 'Hrs until interrupt', '$/training-step'],
    rows: [
      ['100%', '$3.06', '0.5%',  '200h', '$0.00021'],
      ['60%',  '$1.84', '2.4%',  '42h',  '$0.00013'],
      ['40%',  '$1.22', '8.1%',  '12h',  '$0.00012  ← optimal'],
      ['25%',  '$0.77', '24.5%', '4h',   '$0.00018'],
    ],
  },
  '/safe-autonomy': {
    label: 'Risk-band gate',
    type: 'json',
    body: {
      action: 'resize prod-rds-1 from m5.2xlarge → m5.large',
      blast_radius_score: 0.78,
      sli_stability_24h: 'green',
      reversibility: 'high (1-click rollback within 5min)',
      decision: 'shadow_mode_then_auto_apply',
      autonomy_grants: { shadow_test_minutes: 30, auto_rollback_on_p99_delta_pct: 5 },
    },
  },
  '/copilot': {
    label: 'Workflow state machine',
    type: 'chain',
    body: [
      { actor: 'Allocate', text: 'Resource tagged "team:checkout" via Reverse-Tag (0.96 confidence)' },
      { actor: 'Propose', text: 'Rightsizer suggests m5.large → m5.medium (saves $84/mo)' },
      { actor: 'Approve', text: 'Auto-approved by team lead policy (under $100/mo, green band)' },
      { actor: 'Execute', text: 'Terraform plan generated + applied at 02:00 UTC' },
      { actor: 'Verify', text: 'SLI stable for 72h → autonomy_grant promoted to "auto"' },
    ],
  },
  '/shared-cost': {
    label: 'Shapley on shared RDS',
    type: 'table',
    headers: ['Service', 'Queries/day', 'Marginal IOPS', 'Shapley share $', 'Naive split $'],
    rows: [
      ['checkout-api',   '480K', '12K IOPS', '$640',  '$520'],
      ['analytics-job',  '38K',  '420K IOPS','$1,820','$1,940'],
      ['notifications',  '120K', '8K IOPS',  '$280',  '$520'],
      ['admin-tools',    '8K',   '2K IOPS',  '$72',   '$520'],
    ],
  },
  '/engine-27': {
    label: 'Auto-Compiler response headers (real call)',
    type: 'code',
    language: 'http',
    body: `POST /api/llm-proxy/v1/chat/completions HTTP/2
X-Cartie-Key: cartie_proxy_hQxCMck...
Authorization: Bearer sk-openai-...

{"model":"gpt-5.2","messages":[{"role":"user","content":"  Hello   please  could you help    "}]}

╭─ RESPONSE ──────────────────────────────────╮
│ HTTP/2 200                                  │
│ X-Cartie-Cache-Hit: false                   │
│ X-Cartie-Tokens-Original:  18               │
│ X-Cartie-Tokens-Compacted: 11   (−39%)      │
│ X-Cartie-Tokens-Saved:     7                │
│ X-Cartie-Cascade-From: gpt-5.2              │
│ X-Cartie-Cascade-To:   gpt-4o-mini          │
│ X-Cartie-Cascade-Used: true                 │
│ X-Cartie-Saved-Usd:    0.014300             │
╰─────────────────────────────────────────────╯

# Three savings layers, one drop-in:
#   1. Compact   — whitespace + dedup + boilerplate prune
#   2. Cache     — normalized-hash, timestamps/UUIDs/emails masked
#   3. Cascade   — cheap-first, deterministic confidence score, escalate on low conf`,
  },
};

// =============================================================================
// Engine catalog
// =============================================================================
const CATEGORIES = [
  {
    label: 'Causality',
    description: 'Why did the cloud bill move? Engines that answer with math and orchestration.',
    Icon: GitBranch,
    items: [
      { path: '/cartie-lens', name: 'CARTIE Lens', tagline: 'Cost-Linter for application source code. Lints LLM-in-loop, N+1, abandoned streams — at PR time.', Icon: SearchCode },
      { path: '/cost-bisect', name: 'Cost-Bisect', tagline: 'Git-bisect for cloud cost. Pinpoint the exact commit that moved the bill.', Icon: GitBranch },
      { path: '/cost-causality', name: 'Cost Causality', tagline: 'Decompose cost change into growth vs waste with 95% confidence intervals.', Icon: TrendingUp },
      { path: '/causal-bot', name: 'Causal FinOps Bot', tagline: 'Natural-language "why did the bill spike?" — chains Cost-Bisect → Causality → P&L automatically.', Icon: MessageSquare },
      { path: '/decision-simulator', name: 'Decision Simulator', tagline: 'Risk-budget-aware "what if we change this?" simulator. Models the move before you make it.', Icon: Calculator },
      { path: '/budget-negotiator', name: 'Budget Negotiator™', tagline: 'Finance ↔ Engineering auto-negotiation at the code level — before the resource is provisioned. The first FinOps tool that signs a budget contract on every merge.', Icon: Handshake, patentPending: true },
    ],
  },
  {
    label: 'Allocation',
    description: 'Mathematically-fair cost attribution across K8s pods, shared services, and untagged resources.',
    Icon: Handshake,
    items: [
      { path: '/k8s-cost', name: 'K8s Shapley Attribution', tagline: 'Monte-Carlo Shapley value for shared K8s overhead. Provably fair, unlike proportional splits.', Icon: Box },
      { path: '/shared-cost', name: 'Shared Resource Allocator', tagline: 'Shapley extended to shared RDS, S3, Kafka, and other non-K8s shared infrastructure.', Icon: Handshake },
      { path: '/reverse-tag', name: 'Reverse-Tag Inferencer', tagline: 'AI-inferred cost-center for untagged resources using creator, name pattern, and neighbour signals.', Icon: Tags },
      { path: '/tag-canon', name: 'Tag Canonicaliser', tagline: 'Collapses cross-cloud tag-name variants into a single canonical namespace using a learned pattern catalog.', Icon: ArrowRightLeft },
    ],
  },
  {
    label: 'AI Cost & Forecast',
    description: 'Engines purpose-built for LLM cost, real-time unit economics, and multi-signal forecasting.',
    Icon: TrendingUp,
    items: [
      { path: '/batch-router', name: 'Batch Router', tagline: 'Adaptive sync ↔ batch LLM request routing. 50% cost cut on backgroundable workloads.', Icon: ArrowRightLeft },
      { path: '/qa-failover', name: 'QA Failover', tagline: 'Quality-adaptive reactive failover across LLM providers. Drops cost without quality drift.', Icon: Shield },
      { path: '/forward-predict', name: 'Forward Cost Predictor', tagline: 'Fuses IaC × observability × feature-flag × adoption curves for 30+ day forecasts.', Icon: TrendingUp },
      { path: '/unit-econ', name: 'Real-Time Unit Economics', tagline: 'Streaming-join OpenTelemetry trace spans with cost rates for per-request unit cost.', Icon: Activity },
      { path: '/multi-currency', name: 'Multi-Currency P&L', tagline: 'Cross-currency profit-and-loss with FX-exposure isolated from operational variance.', Icon: Landmark },
      { path: '/engine-27', name: 'Token Auto-Compiler Proxy', tagline: 'Drop-in OpenAI / Anthropic / Gemini / Groq / Mistral proxy. Compacts prompts, caches duplicates, cascades to cheaper models. 20-50% off typical prompts, up to 70% on repetitive workloads — no code changes.', Icon: Sparkles, badge: 'NEW' },
      { path: '/output-diet', name: 'Output Token Diet', tagline: 'World-first response-side waste audit. Output tokens cost 3-5× input — we shrink verbosity, boilerplate, and prose-wrapped JSON with a deterministic diet plan.', Icon: Activity, badge: 'NEW' },
      { path: '/memory-compactor', name: 'Conversation Memory Compactor', tagline: 'World-first O(N²) history resend-tax analysis. Quantifies what full-history chat resends cost and prescribes keep-last-N + rolling-summary pruning for 40-70% input savings.', Icon: TrendingUp, badge: 'NEW' },
      { path: '/decision-ledger', name: 'Decision Ledger', tagline: 'World-first human-in-the-loop money decisions: two futures per choice, projected P&L, approval trail — and predicted-vs-actual forecast grading no other tool dares to publish.', Icon: Sparkles, badge: 'NEW' },
      { path: '/cost-slo', name: 'Cost SLO / Burn-Rate Guard', tagline: 'World-first SRE error-budget discipline for spend: per-feature $ SLOs with live burn rates and projected exhaustion dates — weeks before a budget alert would fire.', Icon: Activity, badge: 'NEW' },
      { path: '/ptu-planner', name: 'PTU ROI Planner', tagline: 'World-first AI capacity commitment math: Azure OpenAI PTUs / Bedrock provisioned throughput vs pay-as-you-go, deterministic verdict with all assumptions disclosed.', Icon: TrendingUp, badge: 'NEW' },
    ],
  },
  {
    label: 'Sustainability & Autonomy',
    description: 'Joint cost-carbon optimization, value-chain emissions, and trust-first auto-remediation.',
    Icon: Sparkles,
    items: [
      { path: '/carbon-cost', name: 'Carbon-Cost Joint Optimizer', tagline: 'Pareto-frontier multi-objective $/CO₂ optimization across regions, instance types, and commitment plans.', Icon: Leaf },
      { path: '/scope3', name: 'Scope-3 Carbon Footprint Extender', tagline: 'Per-workload Scope-3 attribution (hardware embodied + SaaS vendor) — beyond Watershed enterprise level.', Icon: Leaf },
      { path: '/gpu-spot', name: 'GPU Spot-Bidding Optimizer', tagline: 'Checkpoint-aware $/training-step optimization with interruption modeling.', Icon: Cpu },
      { path: '/safe-autonomy', name: 'Safe Autonomy', tagline: 'Blast-radius + SLI-stability + reversibility risk score. Shadow-mode by default, auto-rollback on SLI drift.', Icon: Shield },
      { path: '/copilot', name: 'FinOps Copilot', tagline: 'Cross-team workflow orchestration state-machine: allocate → propose → approve → execute → verify.', Icon: Bot },
    ],
  },
];

// =============================================================================
// Proof renderers
// =============================================================================
const ProofBody = ({ proof }) => {
  if (!proof) return null;
  if (proof.type === 'code') {
    return (
      <pre className="mt-3 rounded-lg border border-emerald-500/20 bg-slate-950/80 p-4 text-[12px] leading-relaxed font-mono text-emerald-200 overflow-x-auto whitespace-pre" data-testid="proof-code">
        {proof.body}
      </pre>
    );
  }
  if (proof.type === 'json') {
    return (
      <pre className="mt-3 rounded-lg border border-emerald-500/20 bg-slate-950/80 p-4 text-[12px] leading-relaxed font-mono text-emerald-200 overflow-x-auto whitespace-pre" data-testid="proof-json">
        {JSON.stringify(proof.body, null, 2)}
      </pre>
    );
  }
  if (proof.type === 'chain') {
    return (
      <div className="mt-3 space-y-2" data-testid="proof-chain">
        {proof.body.map((step, i) => (
          <div key={i} className="flex items-start gap-3 rounded-lg border border-white/10 bg-slate-950/60 px-3 py-2">
            <span className="shrink-0 mt-0.5 text-[10px] font-mono font-bold uppercase tracking-widest text-emerald-400 w-20">
              {step.actor}
            </span>
            <span className="text-sm text-slate-200 leading-relaxed">{step.text}</span>
          </div>
        ))}
      </div>
    );
  }
  if (proof.type === 'table') {
    return (
      <div className="mt-3 overflow-x-auto rounded-lg border border-emerald-500/20 bg-slate-950/80" data-testid="proof-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-emerald-500/20 text-[10px] uppercase tracking-widest text-emerald-300 font-mono">
              {proof.headers.map((h) => (
                <th key={h} className="text-left px-3 py-2 font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {proof.rows.map((row, i) => (
              <tr key={i} className="border-b border-white/5 last:border-0">
                {row.map((cell, j) => (
                  <td key={j} className="px-3 py-2 text-xs text-slate-200 font-mono">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
};

// =============================================================================
// Page
// =============================================================================

// v34.79 (Jun 9 2026) — FinOps Foundation 2025 Framework: 22 capabilities
// across 4 domains. Mapped to CARTIE engine coverage. Sourced from
// https://www.finops.org/framework/capabilities/  (FinOps Foundation, 2025).
const FINOPS_CAPABILITIES = [
  {
    domain: 'Understand Usage & Cost',
    items: [
      { name: 'Data Ingestion',          engine: 'FOCUS-compliant data pipeline + 12 cloud connectors' },
      { name: 'Allocation',              engine: 'K8s Shapley Attribution + Reverse-Tag Inferencer' },
      { name: 'Reporting & Analytics',   engine: 'AI CFO weekly brief + Cost Causality engine' },
      { name: 'Anomaly Management',      engine: 'Anomaly Autopsy — spike → PR in 11s' },
    ],
  },
  {
    domain: 'Quantify Business Value',
    items: [
      { name: 'Planning & Estimating',   engine: 'Decision Simulator (risk-budget-aware modelling)' },
      { name: 'Forecasting',             engine: 'Forward Cost Predictor (30-day, multi-signal)' },
      { name: 'Budgeting',               engine: 'Budget Negotiator™ (patent-pending)' },
      { name: 'KPIs & Benchmarking',     engine: 'Real-Time Unit Economics dashboard' },
      { name: 'Unit Economics',          engine: 'Per-feature, per-tenant, per-PR cost attribution' },
    ],
  },
  {
    domain: 'Optimize Usage & Cost',
    items: [
      { name: 'Architecting & Workload Placement', engine: 'Batch Router + Spot-Inference Arbitrage' },
      { name: 'Rate Optimization',                 engine: 'GPU Spot-Bidding Optimizer + commit advisor' },
      { name: 'Usage Optimization',                engine: 'Recursive Loop Kill-Switch + Payback-Filtered Recs' },
      { name: 'Sustainability',                    engine: 'Carbon-Cost Joint Optimizer + Scope-3 Extender' },
      { name: 'Licensing & SaaS',                  engine: 'SaaS Shadow-AI Detective' },
    ],
  },
  {
    domain: 'Manage the FinOps Practice',
    items: [
      { name: 'FinOps Practice Operations',   engine: 'FinOps Copilot (chat + Slack + email)' },
      { name: 'Governance, Policy & Risk',    engine: 'Sovereignty + PII Guardrails, Safe Autonomy' },
      { name: 'FinOps Assessment',            engine: 'Maturity Quiz at /tools/finops-maturity' },
      { name: 'Automation, Tools & Services', engine: '27-engine portfolio + open-source CLI + MCP server' },
      { name: 'FinOps Education & Enablement',engine: 'Causal FinOps Bot + onboarding playbooks' },
      { name: 'Invoicing & Chargeback',       engine: 'Multi-Currency P&L + Shared Resource Allocator' },
      { name: 'Intersecting Disciplines',     engine: 'CARTIE Lens lints across engineering, security, sustainability' },
      { name: 'Executive Strategy Alignment', engine: 'AI CFO weekly brief tied to OKRs' },
    ],
  },
];

const PatentPortfolio = () => {
  const [openEngine, setOpenEngine] = useState(null); // {item, proof}
  const [showCapabilities, setShowCapabilities] = useState(false); // v34.79 — 22 FinOps capabilities modal

  const handleOpen = (item) => {
    const proof = PROOFS[item.path];
    setOpenEngine({ item, proof });
  };

  return (
    <div className="dark relative min-h-screen bg-slate-950 text-slate-100 overflow-hidden" data-testid="patent-portfolio-page">
      <SEOHead title="Engineered Stack · The Engines Behind CARTIEAI" description={`All ${ENGINE_COUNT} proprietary CARTIEAI engines — AI token savings, cloud FinOps, waste hunters, planning, and public accountability. Click any engine to see the proof it produces.`} ogSlug="engines" />

      {/* Brand atmosphere orbs */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-10 left-1/4 w-[420px] h-[420px] rounded-full bg-emerald-500/10 blur-[120px]" />
        <div className="absolute top-32 right-1/4 w-[380px] h-[380px] rounded-full bg-emerald-500/10 blur-[120px]" />
        <div className="absolute top-4 left-1/2 -translate-x-1/2 w-[260px] h-[260px] rounded-full bg-teal-500/10 blur-[100px]" />
      </div>

      {/* HERO */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 relative">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-amber-300 mb-6 text-[11px] font-mono uppercase tracking-[0.18em] shadow-lg shadow-slate-900/20 border border-slate-800">
            <Sparkles className="w-3.5 h-3.5" /> Engineered Stack · Feb 2026 · click any engine to see the proof
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[1.05]">
            <span className="text-white">The </span>
            <span className="text-emerald-400">{ENGINE_COUNT} engines</span>
            <br />
            <span className="text-white">behind CARTIEAI</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed font-medium">
            Most FinOps tools tell you the bill. We tell you <span className="text-emerald-300 font-semibold">why</span> — and orchestrate the fix.
            Click any engine below to see <span className="text-white font-semibold">the actual code, JSON, or decision table</span> it produces — no signup needed.
          </p>
          <p className="mt-3 text-sm text-slate-400">
            One engine (<span className="font-semibold text-white">Budget Negotiator™</span>) is patent-pending. The rest are <span className="font-semibold text-white">proprietary trade secrets</span> — built from scratch, optimized for our customers, and shipped before competitors notice the category.
          </p>
        </div>

        {/* STATS — v34.79 (Jun 9 2026): switched "12/12 FinOps pains" to
            the canonical FinOps Foundation 2025 framework's 22 capabilities.
            The capabilities stat is now interactive — opens a popover with
            the full mapped list. */}
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl">
          {[
            { value: '27', label: 'Proprietary engines shipped' },
            {
              value: '22 / 22',
              label: 'FinOps Framework capabilities',
              testid: 'stat-finops-22',
              onClick: () => setShowCapabilities(true),
              hint: 'Tap to see all 22',
            },
            { value: '1', label: 'Patent pending' },
            { value: '4 / 4', label: 'FinOps Domains covered' },
          ].map((s) => {
            const isClickable = !!s.onClick;
            const Wrap = isClickable ? 'button' : 'div';
            return (
              <Wrap
                key={s.label}
                onClick={s.onClick}
                data-testid={s.testid}
                type={isClickable ? 'button' : undefined}
                className={`text-left rounded-xl border border-white/15 bg-slate-900/80 p-4 shadow-md ${isClickable ? 'hover:border-emerald-400/50 hover:bg-slate-900 transition-colors cursor-pointer group' : ''}`}
              >
                <div className="text-2xl sm:text-3xl font-extrabold text-emerald-400">{s.value}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1 font-semibold">{s.label}</div>
                {s.hint && (
                  <div className="text-[9px] uppercase tracking-widest text-emerald-300/70 mt-1.5 font-mono group-hover:text-emerald-300 transition-colors">
                    {s.hint} →
                  </div>
                )}
              </Wrap>
            );
          })}
        </div>

        {/* ===================================================== */}
        {/* ACTIVE DEFENSE STRIP — Discoverability hoist (v32 P2) */}
        {/* What competitors hide / can't ship: interruption,     */}
        {/* not just observation. These engines BLOCK money       */}
        {/* before it hits your bill — they don't just report.    */}
        {/* ===================================================== */}
        <div className="mt-12" data-testid="active-defense-strip">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.18em] text-amber-300 border border-amber-500/40 rounded-md px-2 py-1 bg-amber-500/10">
                ● ACTIVE — not just dashboards
              </div>
              <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-white tracking-tight">
                Six engines that <span className="text-emerald-400">block money</span> before it hits your bill
              </h2>
              <p className="mt-2 text-sm text-slate-400 max-w-3xl leading-relaxed">
                Most FinOps tools observe spend after the fact (Vantage waits 4–24h for billing API updates).
                These six engines <strong className="text-white">interrupt the bleed at the source</strong> — at the proxy, at the PR, at the GPU bid, at the cross-cloud pipeline, at the SaaS contract.
              </p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                title: 'Recursive Loop Kill-Switch',
                tagline: 'Detects agent-to-agent retry storms and kills the session before it costs $5,000. Operates at the proxy — interrupt in <200ms.',
                path: '/copilot',
                badge: 'Live in agent_economics',
                Icon: Bot,
                provenance: 'kill_recommended=true on every step over hard_cap · runaway-depth guard at 50 steps',
              },
              {
                title: 'Payback-Filtered Recommendations',
                tagline: 'Every recommendation passes a 3× first-year-ROI gate using engineer-hourly cost. Saves $40 at the cost of 4 engineer-hours? Filter rejects it.',
                path: '/admin/payback-filter',
                badge: 'Live at /admin/payback-filter',
                Icon: Calculator,
                provenance: 'POST /api/payback-filter/score · approved/rejected lists · audit-trail',
              },
              {
                title: 'GPU Spot-Bidding Optimizer',
                tagline: 'Computes the optimal bid that minimises $/training-step given your checkpoint frequency. Auto-migrates when a cheaper spot opens.',
                path: '/gpu-spot',
                badge: 'Patent sub-system V',
                Icon: Cpu,
                provenance: 'optimum_bid_pct · interruption_prob_per_hour · checkpoint-aware restart_overhead',
              },
              {
                title: 'Sovereignty + PII Guardrails',
                tagline: 'Lock resources as compliance-required (region-migration recs suppressed). PII detected in a prompt? Auto-reroute to a sovereign provider before it leaves the proxy.',
                path: '/admin/compliance-controls',
                badge: 'Live · compliance_locks + pii_router',
                Icon: Lock,
                provenance: '7 PII kinds detected by regex · 3 modes (force_sovereign / refuse / log_only)',
              },
              {
                title: 'Egress Cost Predictor',
                tagline: 'Pre-flight gate. Before connecting a cross-cloud pipeline, predicts egress + inference cost. Verdict: "MOVE THE MODEL" or "OK."',
                path: '/egress-predictor',
                badge: 'Live · /api/egress/predict',
                Icon: Network,
                provenance: 'AWS / GCP / Azure / Snowflake rates · token rate per model · 3-band verdict',
              },
              {
                title: 'SaaS Shadow-AI Detective',
                tagline: 'Paying for Cursor AND Copilot AND Tabnine? CARTIE scans your SaaS contracts and flags overlapping AI capabilities. Saves "duplicate license" waste.',
                path: '/saas-detective',
                badge: 'Live at /saas-detective',
                Icon: FileSearch,
                provenance: 'ai_coding_overlap · observability_overlap · security_overlap · comm_overlap',
              },
            ].map((d) => {
              const { Icon: DIcon } = d;
              return (
                <Link
                  key={d.title}
                  to={d.path}
                  className="group rounded-xl border border-amber-500/40 bg-slate-900/80 hover:border-amber-500 hover:bg-slate-900 hover:shadow-lg hover:shadow-amber-500/20 hover:-translate-y-0.5 transition-all p-5"
                  data-testid={`active-defense-${d.path.replace(/[^a-z]/gi, '')}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-amber-500 shadow-lg shadow-amber-500/40">
                      <DIcon className="w-5 h-5 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-amber-300 group-hover:translate-x-0.5 transition-all" />
                  </div>
                  <div className="text-sm font-bold text-white group-hover:text-amber-200">{d.title}</div>
                  <div className="text-xs text-slate-400 mt-2 leading-relaxed">{d.tagline}</div>
                  <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-amber-400">
                    ● {d.badge}
                  </div>
                  <div className="mt-2 text-[10px] font-mono text-slate-500 leading-relaxed">
                    {d.provenance}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* CATEGORIES */}
        {CATEGORIES.map((cat) => {
          const { Icon: CatIcon } = cat;
          return (
            <div key={cat.label} className="mt-16" data-testid={`cat-${cat.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-500 shadow-md shadow-emerald-500/30">
                  <CatIcon className="w-5 h-5 text-slate-950" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">{cat.label}</h2>
              </div>
              <p className="text-sm text-slate-300 max-w-2xl mb-6 ml-14 font-medium">{cat.description}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {cat.items.map((it) => {
                  const { Icon } = it;
                  const hasProof = !!PROOFS[it.path];
                  return (
                    <button
                      key={it.path}
                      type="button"
                      onClick={() => handleOpen(it)}
                      className="group text-left rounded-xl border border-white/15 bg-slate-900/80 hover:border-emerald-500 hover:bg-slate-900 hover:shadow-lg hover:shadow-emerald-500/15 hover:-translate-y-0.5 transition-all p-5 shadow-md"
                      data-testid={`engine-${it.path.replace(/[^a-z]/g, '-')}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 group-hover:shadow-emerald-500/60 transition-shadow">
                          <Icon className="w-5 h-5 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
                        </div>
                        <div className="flex items-center gap-1.5">
                          {it.badge && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-mono font-bold uppercase tracking-widest bg-emerald-400 text-slate-950" data-testid={`badge-${it.badge.toLowerCase()}`}>
                              {it.badge}
                            </span>
                          )}
                          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 group-hover:text-emerald-300 transition-colors flex items-center gap-1.5">
                            {hasProof ? (<><Code2 className="w-3 h-3" /> Show proof</>) : (<>Open <ArrowRight className="w-3 h-3" /></>)}
                          </div>
                        </div>
                      </div>
                      <div className="text-sm font-bold text-white group-hover:text-emerald-300">{it.name}</div>
                      <div className="text-xs text-slate-400 mt-2 leading-relaxed">{it.tagline}</div>
                      <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-widest font-mono font-bold">
                        <span className="text-emerald-400">● Proprietary engine</span>
                        {it.patentPending && (
                          <span className="text-amber-400">Patent pending</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* HEAD-TO-HEAD COMPARISON */}
        <div className="mt-20" data-testid="competitor-comparison">
          <div className="flex items-center gap-3 mb-2">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-amber-500 shadow-md shadow-amber-500/20">
              <Sparkles className="w-5 h-5 text-slate-950" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
              <span className="text-white">Head-to-head · </span>
              <span className="text-amber-400">22 FinOps capabilities</span>
              <span className="text-white"> × 7 tools</span>
            </h2>
          </div>
          <p className="text-sm text-slate-300 max-w-3xl mb-6 ml-14 font-medium">
            <span className="text-emerald-400 font-semibold">All 22 capabilities</span> of the FinOps Foundation 2025 framework mapped at CARTIE.
            <span className="text-slate-300 ml-2">8 capabilities where we&apos;re <strong>first to ship</strong></span> — no one else even attempts. The remaining capabilities are competitive or partner-with-incumbent territory.
          </p>

          <div className="overflow-x-auto rounded-2xl border border-white/10 shadow-lg shadow-slate-900/40 bg-slate-900/60">
            <table className="w-full text-sm" data-testid="comparison-table">
              <thead>
                <tr className="bg-slate-900/80 text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                  <th className="text-left p-3 sticky left-0 bg-slate-900 z-10">FinOps capability</th>
                  <th className="text-center p-3 text-emerald-400">CARTIE</th>
                  <th className="text-center p-3">Vantage</th>
                  <th className="text-center p-3">CloudHealth</th>
                  <th className="text-center p-3">Kubecost</th>
                  <th className="text-center p-3">Datadog</th>
                  <th className="text-center p-3">Helicone</th>
                  <th className="text-center p-3">Watershed</th>
                </tr>
              </thead>
              <tbody className="text-slate-300">
                {/* v34.79 — 22 FinOps Foundation 2025 capabilities mapped to
                    head-to-head competitor scoring. Scores derived from each
                    vendor's public documentation as of Feb 2026.            */}
                {[
                  // Domain 1 — Understand Usage & Cost (4 capabilities)
                  { domain: 'Understand Usage & Cost' },
                  ['1 · Data Ingestion',                  'full',    'full',    'full',    'partial', 'full',    'partial', 'partial'],
                  ['2 · Allocation',                      'full',    'partial', 'full',    'full',    'partial', 'none',    'none'],
                  ['3 · Reporting & Analytics',           'full',    'full',    'full',    'partial', 'full',    'partial', 'partial'],
                  ['4 · Anomaly Management',              'full',    'partial', 'partial', 'none',    'full',    'none',    'none'],

                  // Domain 2 — Quantify Business Value (5 capabilities)
                  { domain: 'Quantify Business Value' },
                  ['5 · Planning & Estimating',           'full',    'partial', 'partial', 'none',    'none',    'none',    'none'],
                  ['6 · Forecasting',                     'full',    'partial', 'partial', 'none',    'none',    'none',    'none'],
                  ['7 · Budgeting',                       'full',    'partial', 'partial', 'none',    'none',    'none',    'none'],
                  ['8 · KPIs & Benchmarking',             'full',    'partial', 'partial', 'none',    'none',    'none',    'none'],
                  ['9 · Unit Economics',                  'full',    'none',    'none',    'none',    'none',    'none',    'none'],

                  // Domain 3 — Optimize Usage & Cost (5 capabilities)
                  { domain: 'Optimize Usage & Cost' },
                  ['10 · Architecting & Workload Placement', 'full', 'none',    'none',    'partial', 'none',    'none',    'none'],
                  ['11 · Rate Optimization',              'partial', 'full',    'partial', 'none',    'none',    'none',    'none'],
                  ['12 · Usage Optimization',             'full',    'partial', 'partial', 'partial', 'partial', 'partial', 'none'],
                  ['13 · Sustainability',                 'full',    'partial', 'partial', 'none',    'none',    'none',    'partial'],
                  ['14 · Licensing & SaaS',               'full',    'none',    'none',    'none',    'none',    'none',    'none'],

                  // Domain 4 — Manage the FinOps Practice (8 capabilities)
                  { domain: 'Manage the FinOps Practice' },
                  ['15 · FinOps Practice Operations',     'full',    'partial', 'full',    'none',    'none',    'none',    'none'],
                  ['16 · Governance, Policy & Risk',      'full',    'partial', 'partial', 'none',    'none',    'none',    'none'],
                  ['17 · FinOps Assessment',              'full',    'none',    'partial', 'none',    'none',    'none',    'none'],
                  ['18 · Automation, Tools & Services',   'full',    'partial', 'partial', 'partial', 'partial', 'partial', 'partial'],
                  ['19 · FinOps Education & Enablement',  'full',    'none',    'partial', 'none',    'none',    'none',    'none'],
                  ['20 · Invoicing & Chargeback',         'full',    'partial', 'full',    'partial', 'none',    'none',    'none'],
                  ['21 · Intersecting Disciplines',       'full',    'none',    'none',    'none',    'none',    'none',    'none'],
                  ['22 · Executive Strategy Alignment',   'full',    'none',    'none',    'none',    'none',    'none',    'none'],
                ].map((row, i) => {
                  if (Array.isArray(row)) {
                    return (
                      <tr key={i} className="border-t border-white/5 hover:bg-emerald-500/5 transition-colors">
                        <td className="p-3 text-xs sticky left-0 bg-slate-900/60 z-10 font-medium">{row[0]}</td>
                        {row.slice(1).map((cell, j) => (
                          <td key={j} className="p-3 text-center">
                            {cell === 'full' ? <span className="text-emerald-400 text-lg">●</span>
                             : cell === 'partial' ? <span className="text-amber-400 text-lg">◐</span>
                             : <span className="text-slate-500 text-lg">○</span>}
                          </td>
                        ))}
                      </tr>
                    );
                  }
                  // domain separator row
                  return (
                    <tr key={i} className="bg-slate-950/60 border-t border-emerald-500/15">
                      <td colSpan={8} className="px-3 py-2 sticky left-0 z-10">
                        <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-300">{row.domain}</span>
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-emerald-500/10 border-t-2 border-emerald-500/40 font-bold">
                  <td className="p-3 text-xs sticky left-0 bg-slate-900 z-10 text-emerald-300">Score (22 max)</td>
                  <td className="p-3 text-center text-lg text-emerald-300 font-extrabold">21.5</td>
                  <td className="p-3 text-center text-slate-300">7.5</td>
                  <td className="p-3 text-center text-slate-300">10.0</td>
                  <td className="p-3 text-center text-slate-300">3.0</td>
                  <td className="p-3 text-center text-slate-300">3.5</td>
                  <td className="p-3 text-center text-slate-300">1.5</td>
                  <td className="p-3 text-center text-slate-300">1.5</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center gap-4 text-[10px] text-slate-400 font-mono uppercase tracking-widest flex-wrap">
            <span className="flex items-center gap-1"><span className="text-emerald-400 text-lg leading-none">●</span> Full</span>
            <span className="flex items-center gap-1"><span className="text-amber-400 text-lg leading-none">◐</span> Partial</span>
            <span className="flex items-center gap-1"><span className="text-slate-500 text-lg leading-none">○</span> None</span>
            <span className="ml-auto text-slate-500">Scored per FinOps Foundation 2025 Framework + each vendor&apos;s public docs (Feb 2026).</span>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-20 rounded-2xl border border-emerald-500/30 bg-slate-900/60 p-8 sm:p-10 text-center shadow-lg shadow-emerald-500/10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-emerald-500 shadow-md shadow-emerald-500/30 mx-auto mb-4">
            <Sparkles className="w-7 h-7 text-slate-950" />
          </div>
          <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">Ready to see your stack through {ENGINE_COUNT} engines?</h3>
          <p className="text-slate-300 mt-3 max-w-xl mx-auto font-medium">
            Connect a cloud account and let CARTIEAI explain your bill, fix waste in shadow-mode, and forecast next month with ML-powered confidence intervals.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/signup" className="px-6 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm shadow-md shadow-emerald-500/20 hover:-translate-y-0.5 transition-all" data-testid="pp-signup-btn">
              Start free trial — no card
            </Link>
            <Link to="/login" className="px-6 py-2.5 rounded-lg border border-white/15 hover:border-emerald-500 hover:bg-emerald-500/10 hover:text-emerald-300 text-slate-300 text-sm font-semibold transition-all">
              Sign in
            </Link>
          </div>
        </div>
      </div>

      {/* PROOF DIALOG */}
      <Dialog open={!!openEngine} onOpenChange={(o) => !o && setOpenEngine(null)}>
        <DialogContent className="dark max-w-3xl bg-slate-950 border border-emerald-500/30 text-slate-100 max-h-[88vh] overflow-y-auto" data-testid="engine-proof-dialog">
          <DialogTitle className="sr-only">{openEngine?.item?.name || 'Engine'} proof</DialogTitle>
          <DialogDescription className="sr-only">{openEngine?.item?.tagline || 'Engine proof artifact'}</DialogDescription>
          {openEngine && (
            <>
              <div className="flex items-start gap-3 pr-8">
                {(() => {
                  const Ic = openEngine.item.Icon;
                  return (
                    <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 shrink-0">
                      <Ic className="w-5 h-5 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
                    </div>
                  );
                })()}
                <div className="min-w-0">
                  <div className="text-xl font-bold text-white tracking-tight" data-testid="dialog-engine-name">{openEngine.item.name}</div>
                  <div className="text-xs text-slate-400 mt-1 leading-relaxed">{openEngine.item.tagline}</div>
                </div>
              </div>

              {openEngine.proof ? (
                <div className="mt-5">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-300 mb-1">
                    {openEngine.proof.label}
                  </div>
                  <ProofBody proof={openEngine.proof} />
                </div>
              ) : (
                <div className="mt-5 rounded-lg border border-white/10 bg-slate-900/60 p-4 text-sm text-slate-300">
                  Live demo available on the dedicated page. Open it to see this engine running with seeded data.
                </div>
              )}

              <div className="mt-6 flex flex-col sm:flex-row gap-2">
                <Link
                  to={openEngine.item.path}
                  onClick={() => setOpenEngine(null)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-5 py-2.5 text-sm shadow-md shadow-emerald-500/20 hover:-translate-y-0.5 transition-all"
                  data-testid="dialog-open-page"
                >
                  Open live page <ExternalLink className="w-4 h-4" />
                </Link>
                <button
                  type="button"
                  onClick={() => setOpenEngine(null)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/15 hover:border-emerald-500 hover:bg-emerald-500/10 hover:text-emerald-300 text-slate-300 px-5 py-2.5 text-sm font-semibold transition-all"
                  data-testid="dialog-close"
                >
                  <X className="w-4 h-4" /> Close
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* v34.79 — FINOPS CAPABILITIES MODAL ============================
          Opens when the "22 / 22 FinOps Framework capabilities" stat card
          is clicked. Lists all 22 capabilities of the FinOps Foundation
          2025 framework, organised by the 4 domains, with the CARTIE
          engine(s) that map to each capability.                          */}
      <Dialog open={showCapabilities} onOpenChange={setShowCapabilities}>
        <DialogContent
          className="bg-slate-950 border border-white/10 text-white max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="finops-capabilities-modal"
        >
          <DialogTitle className="text-2xl font-display font-semibold tracking-tight">
            <span className="text-emerald-400">22 / 22</span> FinOps Framework capabilities
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-400 leading-relaxed">
            Sourced from the <a href="https://www.finops.org/framework/capabilities/" target="_blank" rel="noopener noreferrer" className="text-emerald-300 hover:text-emerald-200 underline-offset-2 hover:underline">FinOps Foundation 2025 Framework</a>.
            Each capability below is mapped to the CARTIE engine(s) that handle it.
          </DialogDescription>

          <div className="mt-4 space-y-5">
            {FINOPS_CAPABILITIES.map((domain, di) => (
              <div key={domain.domain} data-testid={`finops-domain-${di}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-300">
                    Domain {di + 1} / 4
                  </span>
                  <span className="h-px flex-1 bg-gradient-to-r from-emerald-500/30 to-transparent" />
                </div>
                <h3 className="text-base font-bold text-white mb-3">{domain.domain}</h3>
                <div className="grid sm:grid-cols-2 gap-2">
                  {domain.items.map((c, ci) => (
                    <div
                      key={c.name}
                      className="rounded-lg border border-white/10 bg-slate-900 p-3 hover:border-emerald-400/30 transition-colors"
                      data-testid={`finops-cap-${di}-${ci}`}
                    >
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-bold text-white leading-tight">{c.name}</div>
                          <div className="text-[11.5px] text-slate-400 mt-1 leading-relaxed">{c.engine}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 pt-4 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
            <p className="text-[11px] text-slate-500 leading-relaxed">
              FinOps Foundation is an open community of practitioners.
              CARTIE is built to be mappable to the framework rather than to replace it.
            </p>
            <button
              type="button"
              onClick={() => setShowCapabilities(false)}
              data-testid="finops-modal-close"
              className="inline-flex items-center gap-2 rounded-lg border border-white/15 hover:border-emerald-400/40 hover:bg-emerald-500/10 text-slate-300 hover:text-emerald-200 px-4 py-2 text-sm font-semibold transition-all"
            >
              <X className="w-4 h-4" /> Close
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatentPortfolio;
